import { useEffect, useMemo, useRef, useState } from 'react'
import { RefreshCw } from 'lucide-react'
import { useRewardClaimReplayState } from '@/store/selectors'
import { getPlantGardenV2Runtime } from '@/repositories/plant-garden-v2'
import { deliverRewardClaim } from './deliverRewardClaim'
import { BASE_RETRY_DELAY_MS, getRewardClaimRetryDelayMs } from './rewardClaimRetry'
import './rewardClaimReplayer.css'

export default function RewardClaimReplayer() {
  const {
    rewardClaims,
    markRewardClaimPlantGrantedAndCommit,
    recordRewardClaimDeliveryFailureAndCommit,
    completeRewardClaimDeliveryAndCommit,
  } = useRewardClaimReplayState()
  const [retryTick, setRetryTick] = useState(0)
  const replayingRef = useRef(false)
  const pending = useMemo(
    () => rewardClaims.filter((claim) => claim.status === 'pending'),
    [rewardClaims],
  )

  useEffect(() => {
    if (replayingRef.current) return
    const now = Date.now()
    const ready = pending.filter((claim) => {
      if (!claim.nextDeliveryAttemptAt) return true
      const retryAt = new Date(claim.nextDeliveryAttemptAt).getTime()
      return !Number.isFinite(retryAt) || retryAt <= now
    })
    if (ready.length === 0) {
      const retryAt = Math.min(...pending.flatMap((claim) => {
        const value = claim.nextDeliveryAttemptAt
          ? new Date(claim.nextDeliveryAttemptAt).getTime()
          : Number.NaN
        return Number.isFinite(value) ? [value] : []
      }))
      if (!Number.isFinite(retryAt)) return
      const timer = window.setTimeout(
        () => setRetryTick((value) => value + 1),
        Math.max(0, retryAt - now),
      )
      return () => window.clearTimeout(timer)
    }

    const runtime = getPlantGardenV2Runtime()
    replayingRef.current = true
    void (async () => {
      for (const claim of ready) {
        try {
          await deliverRewardClaim(claim, {
            plantGarden: runtime,
            onPlantExperienceGranted: (completedAt) => (
              markRewardClaimPlantGrantedAndCommit(claim.id, completedAt)
            ),
          })
          await completeRewardClaimDeliveryAndCommit(claim.id)
        } catch (error) {
          const attemptedAt = new Date().toISOString()
          const nextAttemptAt = new Date(
            Date.now() + getRewardClaimRetryDelayMs((claim.deliveryAttemptCount ?? 0) + 1),
          ).toISOString()
          await recordRewardClaimDeliveryFailureAndCommit({
            claimId: claim.id,
            error: error instanceof Error ? error.message : '奖励同步失败',
            attemptedAt,
            nextAttemptAt,
          }).catch(async (recordError) => {
            console.error('Reward claim failure state could not be persisted.', recordError)
            await new Promise((resolve) => window.setTimeout(resolve, BASE_RETRY_DELAY_MS))
          })
        }
      }
    })().finally(() => {
      replayingRef.current = false
      setRetryTick((value) => value + 1)
    })
  }, [
    completeRewardClaimDeliveryAndCommit,
    markRewardClaimPlantGrantedAndCommit,
    pending,
    recordRewardClaimDeliveryFailureAndCommit,
    retryTick,
  ])

  if (pending.length === 0) return null
  const delayed = pending.some((claim) => Boolean(claim.lastDeliveryError))
  return (
    <div className={`reward-sync-status ${delayed ? 'is-delayed' : ''}`} role="status" aria-live="polite">
      <RefreshCw aria-hidden="true" />
      <span>{delayed ? '奖励同步暂时中断，系统会自动重试' : '正在同步学习奖励'}</span>
    </div>
  )
}
